import { memo, useCallback, useState, useMemo, useEffect } from 'react';
import moment from 'moment';
import { Icon, useTheme } from 'hoi-poi-ui';

import { getTrackingEvents } from 'services/EntityService';
import {
    ACTIVITIES,
    COMPANIES,
    CONTACTS,
    OPPORTUNITIES,
    SALESORDERS,
    AGENDA,
} from 'constants/Entities';

import { getLiteral } from 'utils/getLiteral';
import { logEvent } from 'utils/tracking';
import { getActivityDateFormat } from 'utils/dates';

import useEntityDetail from 'containers/components/EntityDetail/hooks/useEntityDetail';

import DashboardWidget from '../components/DashboardWidget';
import DashboardWidgetRow from '../components/DashboardWidgetRow';

const OBJECT_ID_ENTITY = [ACTIVITIES, COMPANIES, CONTACTS, OPPORTUNITIES, SALESORDERS, AGENDA];
const EMPTY_SVG = (
    <svg xmlns="http://www.w3.org/2000/svg" width={116} height={101} fill="none">
        <path
            fill="#F4F5F6"
            d="M71.987 66.37H43.598c-2.386 0-4.327-1.954-4.327-4.357V33.426c0-2.403 1.941-4.358 4.327-4.358h28.389c2.386 0 4.328 1.955 4.328 4.358v28.587c0 2.403-1.942 4.358-4.328 4.358ZM43.598 29.766c-2.007 0-3.635 1.64-3.635 3.66v28.588c0 2.021 1.628 3.66 3.635 3.66h19.886c6.704 0 12.138-5.472 12.138-12.223V33.426c0-2.022-1.627-3.66-3.635-3.66H43.598Z"
        />
        <path
            fill="#F4F5F6"
            d="M46.659 32.738a2.448 2.448 0 0 0-2.437 2.454v3.84a2.448 2.448 0 0 0 2.437 2.454h3.814c1.343 0 2.436-1.1 2.436-2.454v-3.84a2.448 2.448 0 0 0-2.436-2.454h-3.814ZM46.659 43.55a2.448 2.448 0 0 0-2.437 2.453v3.84a2.448 2.448 0 0 0 2.437 2.454h3.814c1.343 0 2.436-1.1 2.436-2.453v-3.84a2.448 2.448 0 0 0-2.436-2.454h-3.814ZM46.659 54.361a2.448 2.448 0 0 0-2.437 2.454v3.84a2.448 2.448 0 0 0 2.437 2.454h3.814c1.343 0 2.436-1.1 2.436-2.454v-3.84a2.448 2.448 0 0 0-2.436-2.454h-3.814Z"
        />
        <path
            fill="#FF8C00"
            d="M49.188 23.368c-1.693 0-3.07 1.387-3.07 3.092v4.839c0 1.705 1.377 3.092 3.07 3.092h4.805c1.694 0 3.07-1.387 3.07-3.092v-4.84c0-1.704-1.377-3.091-3.07-3.091h-4.805ZM65.01 36.891c-1.694 0-3.071 1.387-3.071 3.092v4.84c0 1.704 1.377 3.09 3.07 3.09h4.806c1.693 0 3.07-1.386 3.07-3.09v-4.84c0-1.705-1.377-3.092-3.07-3.092h-4.806Z"
        />
        <path fill="#FFC5AC" d="m58.131 85.6-.976-.009-.432-3.88 1.44.012-.032 3.877Z" />
        <path
            fill="#37526C"
            d="M55.004 87.163a.439.439 0 0 0 .433.443l1.94.017.34-.694.125.697.733.007-.185-2.487-.255-.017-1.04-.072-.335-.023-.006.704-1.559 1.065a.44.44 0 0 0-.191.36Z"
        />
        <path fill="#FFC5AC" d="m63.098 85.6-.976-.009-.432-3.88 1.44.012-.032 3.877Z" />
        <path
            fill="#37526C"
            d="M59.971 87.163a.439.439 0 0 0 .433.443l1.94.017.34-.694.125.697.733.007-.185-2.487-.255-.017-1.04-.072-.335-.023-.005.704-1.56 1.065a.44.44 0 0 0-.191.36ZM54.3 54.227l3.957.126 3.75.84s-.376.46.874 1.719c0 0 1.666 2.936.5 5.788l-.5 11.41s1.457 8.043.344 10.603l-1.472.092-2.49-10.602-1.214-11.419-.583 11.242s2.23 7.716.822 10.78l-1.594-.093-2.727-10.687s-1.167-12.332-1-14.681c.167-2.35 1.333-5.118 1.333-5.118Z"
        />
        <path
            fill="#FFC5AC"
            d="M67.59 36.199a1.02 1.02 0 0 0-.315 1.828v.002L67.2 39.9l1.422.663.114-2.88h-.01a1.02 1.02 0 0 0 .098-.754 1.011 1.011 0 0 0-1.233-.73Z"
        />
        <path
            fill="#AEE5F8"
            d="M59.64 42.88a1.637 1.637 0 0 1-.48-1.143 1.636 1.636 0 0 1 .786-1.433 1.62 1.62 0 0 1 1.836.091l4.467 3.387.796-4.879 1.708.554-.407 6.353a1.277 1.277 0 0 1-.668 1.047 1.26 1.26 0 0 1-1.235-.014l-6.455-3.698a1.66 1.66 0 0 1-.349-.266Z"
        />
        <path
            fill="#FFC5AC"
            d="M52.828 33.47a1.023 1.023 0 0 1-.353 1.396c-.22.132-.47.169-.703.125l-1.255 1.385-1.473-.538 1.933-2.126.007.007a1.008 1.008 0 0 1 1.844-.25Z"
        />
        <path
            fill="#AEE5F8"
            d="m53.37 43.792-7.17-1.95a1.272 1.272 0 0 1-.887-.866 1.283 1.283 0 0 1 .258-1.217l4.152-4.8 1.601.818-2.846 4.029 5.54.761a1.633 1.633 0 0 1 1.368 1.237 1.64 1.64 0 0 1-.444 1.574 1.615 1.615 0 0 1-1.572.414Z"
        />
        <path
            fill="#AEE5F8"
            d="m63.592 55.906-10.026.463c-.276-.421-.201-1.054.184-1.847 1.456-2.994.35-8.657-.393-11.606-.209-.828.351-1.65 1.195-1.735l.945-.095.874-2.687h3.06l1.27 1.461 2.155 1.318c-.392 4.068-2.063 9.813.317 12.694.521.63.669 1.311.419 2.034Z"
        />
        <path
            fill="#FFC5AC"
            d="M57.512 38.461a2.992 2.992 0 0 0 2.981-3.001 2.991 2.991 0 0 0-2.981-3.002 2.991 2.991 0 0 0-2.981 3.002 2.991 2.991 0 0 0 2.981 3.001Z"
        />
        <path
            fill="#37526C"
            d="M60.885 33.538c.415-1.522-4.105-2.828-4.984-1.602-.122-.174-.573-.28-.78-.227-.208.054-.382.188-.552.317-.233.18-.475.368-.619.628-.144.259-.167.611.028.836.154.179.424 1.128.656 1.175.161.033.297.06.414.08.104-.154.37-.346.35-.531.158.105.1.29.069.478-.106.627-2.483 5.486-1.122 4.054.135.08.302.155.494.223.326-.495.594-1.079.77-1.69l.002.012c.06.53.445.966.955 1.115 2.044.596 3.698-.279 4.266-1.857-.208-.426-.299-.38-.28-.392a.488.488 0 0 1 .729.234c.033.087.063.161.089.217.296-1.068.649-.913-.485-3.07Z"
        />
        <path
            fill="#37526C"
            d="m59.475 32.434-.209-1.075c-.017-.09-.034-.189.007-.271.052-.107.185-.15.301-.134.117.017.204.124.32.145.402.072.933-.329 1.078.683.06.423.728.467.952.83.223.361.25.884-.054 1.18-.244.236-.635.264-.95.143-.317-.12-.57-.365-.792-.622-.222-.257-.423-.535-.678-.758"
        />
        <path
            fill="#FF8C00"
            d="M61.044 34.068c-.815-.114-1.371-.413-1.652-.887-.368-.621-.121-1.305-.11-1.334l.172.065c-.003.006-.226.632.097 1.175.251.423.762.691 1.519.798l-.026.183ZM64.872 22.453c-2.398 0-4.349-1.964-4.349-4.38 0-2.414 1.951-4.379 4.35-4.379 2.397 0 4.349 1.965 4.349 4.38s-1.952 4.38-4.35 4.38Zm0-7.007a2.621 2.621 0 0 0-2.61 2.628 2.622 2.622 0 0 0 2.61 2.628 2.622 2.622 0 0 0 2.61-2.628 2.621 2.621 0 0 0-2.61-2.628Z"
        />
        <path
            fill="#FF8C00"
            d="M71.564 25.43a.862.862 0 0 1-.595-.237l-4.082-3.852a.88.88 0 0 1-.04-1.238.864.864 0 0 1 1.23-.04l4.081 3.852a.88.88 0 0 1 .04 1.238.863.863 0 0 1-.634.277Z"
        />
        <path
            fill="#F4F5F6"
            d="M90.013 85.501c.295.019.459-.351.235-.567l-.022-.089.009-.021c.299-.718 1.312-.713 1.609.006.263.638.598 1.278.68 1.953.038.297.021.6-.044.893.616-1.356.94-2.838.94-4.327a10.129 10.129 0 0 0-.204-2.032 10.574 10.574 0 0 0-2.075-4.467 2.796 2.796 0 0 1-1.159-1.21 2.151 2.151 0 0 1-.193-.585c.056.007.212-.858.17-.91.078-.121.22-.18.305-.298.427-.583 1.015-.481 1.322.311.656.333.663.886.26 1.418-.256.338-.291.796-.516 1.158l.07.089c.424.547.79 1.136 1.1 1.754-.087-.687.042-1.515.261-2.048.25-.608.72-1.12 1.133-1.646.496-.631 1.513-.356 1.6.445.002.007.003.015.003.023a2.94 2.94 0 0 0-.18.11.445.445 0 0 0 .178.808l.009.002c-.022.222-.06.443-.116.66.53 2.064-.614 2.815-2.247 2.849l-.108.055c.166.47.298.95.396 1.438a10.35 10.35 0 0 1 .176 2.963l.003-.02a2.718 2.718 0 0 1 .92-1.567c.707-.585 1.708-.8 2.471-1.272.368-.226.84.067.775.496l-.003.02a2.968 2.968 0 0 0-.512.272.445.445 0 0 0 .178.808l.009.001.018.003c-.195.467-.467.9-.807 1.273-.331 1.802-1.755 1.972-3.277 1.448-.167.73-.41 1.443-.723 2.123h-2.58l-.026-.088c.239.015.48.001.714-.043l-.574-.711-.012-.014-.292-.362a4.349 4.349 0 0 1 .126-1.102Z"
        />
        <path
            fill="#D4D8DC"
            d="M18.643 87.828a.17.17 0 0 0 .17.172H97.59a.17.17 0 0 0 .17-.172.17.17 0 0 0-.17-.171H18.813a.17.17 0 0 0-.17.171Z"
        />
    </svg>
);

const RecentlyDashboardWidget = memo(() => {
    const { openTab } = useEntityDetail();
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState();
    const [list, setList] = useState([]);
    const [filter, setFilter] = useState();

    const getList = useCallback(() => {
        setLoading(true);
        setError(false);

        const idObjectType = filter?.value || -1;
        getTrackingEvents({
            idObjectType: String(idObjectType),
            maxRows: 100,
        })
            .then(setList)
            .catch((e) => {
                console.error(e);
                setError(true);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [filter?.value]);

    useEffect(() => {
        getList();
    }, [getList]);

    const onClick = useCallback(
        (item) => {
            const entity = OBJECT_ID_ENTITY.find((e) => e.objectTypeId === item.IdObjectType);

            switch (item.ObjectType) {
                case 'activities':
                    openTab({
                        entity,
                        id: item.EntityId,
                        hasCrudInDetail: true,
                        toTab: true,
                        //info: { activityType: activity.ActivityType },
                        avoidRedirects: true,
                    });
                    break;
                default:
                    openTab({
                        entity,
                        id: item.EntityId,
                        hasCrudInDetail: true,
                        toTab: true,
                        avoidRedirects: true,
                    });
                    break;
            }

            logEvent({
                event: 'dashboard',
                submodule: 'recentlyView',
                functionality: 'detailView',
            });
        },
        [openTab],
    );

    const onChangeFilter = useCallback(
        (filter) => {
            setFilter(filter || entityTypes[0]);
            logEvent({
                event: 'dashboard',
                submodule: 'recentlyView',
                functionality: 'filter',
            });
        },
        [entityTypes],
    );

    const getTypeIcon = useCallback(
        (item) => {
            let name = 'asterisk';
            switch (item.ObjectType) {
                case 'accounts':
                    name = 'accounts';
                    break;
                case 'opportunities':
                    name = 'opportunities';
                    break;
                case 'contacts':
                    name = 'contacts';
                    break;
                case 'activities':
                    name = 'activities';
                    break;
                // case 'users':
                //     name = 'multipleUsers';
                //     break;
                case 'salesorders':
                    name = 'salesOrders';
                    break;
                // case 'products':
                //     name = 'products';
                //     break;
                // case 'goals':
                //     name = 'goals';
                //     break;
                // case 'campaigns':
                //     name = 'campaigns';
                //     break;
                case 'calendar':
                    name = 'event';
                    break;
                default:
                    break;
            }
            return <Icon name={name} size="large" color={theme.colors.neutral600} />;
        },
        [theme.colors.neutral600],
    );

    const entityTypes = useMemo(() => {
        return [
            {
                label: getLiteral('label_all'),
                value: -1,
            },
            {
                label: getLiteral('label_account'),
                value: 2,
                iconType: 'accounts',
            },
            {
                label: getLiteral('label_contact'),
                value: 3,
                iconType: 'contacts',
            },
            {
                label: getLiteral('title_opportunity'),
                value: 1,
                iconType: 'opportunities',
            },
            {
                label: getLiteral('label_activity'),
                value: 4,
                iconType: 'activities',
            },
            {
                label: getLiteral('label_event'),
                value: 10,
                iconType: 'event',
            },
            {
                label: getLiteral('title_orders'),
                value: 55,
                iconType: 'salesOrders',
            },
            // {
            //     label: getLiteral('title_products'),
            //     value: 6,
            //     iconType: 'products',
            // },
            // {
            //     label: getLiteral('label_goal'),
            //     value: 8,
            //     iconType: 'goals',
            // },
            // {
            //     label: getLiteral('label_campaigns'),
            //     value: 9,
            //     iconType: 'campaigns',
            // },
        ];
    }, []);

    const rowList = useMemo(() => {
        return list?.map((i) => (
            <DashboardWidgetRow
                item={i}
                onClick={(e) => onClick(i)}
                leftColumn={getTypeIcon(i)}
                title={i.EntityName || '-'}
                subtitle={getActivityDateFormat(
                    moment.utc(i.CreatedAtUTC, 'YYYY-MM-DDTHH:mm').local(),
                )}
            />
        ));
    }, [getTypeIcon, list, onClick]);

    return (
        <DashboardWidget
            title={getLiteral('title_recently_viewed')}
            // Filter
            filterLabel={getLiteral('label_entity')}
            filterOptions={entityTypes}
            filterDefaultValue={entityTypes[0]}
            onChangeFilter={onChangeFilter}
            // State
            isLoading={loading}
            isEmpty={list?.length === 0 && !error}
            isError={error}
            // Empty view
            emptyTitle={getLiteral('label_empty_widget_recent_view')}
            emptySubtitle={getLiteral('label_empty_widget_recent_view_desc')}
            emptyImage={EMPTY_SVG}
        >
            {rowList}
        </DashboardWidget>
    );
});

export default RecentlyDashboardWidget;
